class GoogleReviewsNav extends HTMLElement {
  connectedCallback() {
    this.carousel = document.querySelector(this.dataset.targetEl);
    if (!this.carousel) return;

    this.list = this.carousel.querySelector('ul');
    if (!this.list) return;

    this.slides = Array.from(this.list.querySelectorAll(':scope > li'));
    if (!this.slides.length) return;

    this.indicatorItems = Array.from(this.querySelectorAll('ul > li'));
    this.currentIndex = 0;

    this.querySelector('.pdp-media-indicators__button_previous')
      ?.addEventListener('click', () => this.step(-1));
    this.querySelector('.pdp-media-indicators__button_next')
      ?.addEventListener('click', () => this.step(1));

    this.indicatorItems.forEach((item, i) => {
      item.querySelector('button')?.addEventListener('click', () => this.goTo(i));
    });

    this.list.addEventListener('scroll', () => {
      clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(() => this.syncFromScroll(), 150);
    }, { passive: true });

    // Wait one frame so layout is settled before reading BCRs
    requestAnimationFrame(() => {
      this.syncIndicatorCount();
      this.updateIndicators();
    });

    window.addEventListener('resize', () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this.currentIndex = Math.min(this.currentIndex, this.lastReachableIndex);
        this.syncIndicatorCount();
        this.updateIndicators();
      }, 200);
    }, { passive: true });
  }

  scrollTargetFor(index) {
    const slide = this.slides[index];
    if (!slide) return 0;
    return (
      this.list.scrollLeft +
      slide.getBoundingClientRect().left -
      this.list.getBoundingClientRect().left
    );
  }

  get maxScroll() {
    return this.list.scrollWidth - this.list.clientWidth;
  }

  // Last slide index that can physically sit at the list's left edge.
  // +2px absorbs sub-pixel rounding from % widths at non-integer container sizes.
  get lastReachableIndex() {
    const max = this.maxScroll;
    for (let i = this.slides.length - 1; i >= 0; i--) {
      if (this.scrollTargetFor(i) <= max + 2) return i;
    }
    return 0;
  }

  // Hide indicator dots for slides that can never be the leftmost —
  // reduces the bar to only the actual number of navigable steps.
  // Hides the whole nav when everything already fits (nothing to scroll).
  syncIndicatorCount() {
    const last = this.lastReachableIndex;
    this.indicatorItems.forEach((item, i) => {
      item.style.display = i <= last ? '' : 'none';
    });
    this.style.visibility = last > 0 ? '' : 'hidden';
  }

  step(dir) {
    const last = this.lastReachableIndex;
    if (dir > 0) {
      if (this.currentIndex >= last) {
        this.currentIndex = 0;
        this.list.scrollTo({ left: 0, behavior: 'smooth' });
        this.updateIndicators();
      } else {
        this.goTo(this.currentIndex + 1);
      }
    } else {
      if (this.list.scrollLeft <= 1) {
        this.list.scrollTo({ left: this.maxScroll, behavior: 'smooth' });
        this.currentIndex = last;
        this.updateIndicators();
      } else {
        this.goTo(Math.max(0, this.currentIndex - 1));
      }
    }
  }

  goTo(index) {
    const last = this.lastReachableIndex;
    const clamped = Math.min(index, last);
    this.currentIndex = clamped;
    // At the last reachable position scroll all the way to maxScroll so the
    // final card reaches the container's right edge and isn't clipped.
    const scrollLeft = clamped >= last ? this.maxScroll : this.scrollTargetFor(clamped);
    this.list.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    this.updateIndicators();
  }

  syncFromScroll() {
    const listLeft = this.list.getBoundingClientRect().left;
    let bestIndex = 0, minDist = Infinity;
    this.slides.forEach((slide, i) => {
      const dist = Math.abs(slide.getBoundingClientRect().left - listLeft);
      if (dist < minDist) { minDist = dist; bestIndex = i; }
    });
    // Clamp to lastReachableIndex — at maxScroll the closest slide by BCR may
    // be beyond last, which would break backward navigation.
    bestIndex = Math.min(bestIndex, this.lastReachableIndex);
    if (bestIndex !== this.currentIndex) {
      this.currentIndex = bestIndex;
      this.updateIndicators();
    }
  }

  updateIndicators() {
    const last = this.lastReachableIndex;
    const clamped = Math.min(this.currentIndex, last);
    this.indicatorItems.forEach((item, i) => {
      if (i > last) return;
      item.classList.toggle('active', i === clamped);
      item.classList.toggle('has-passed', i < clamped);
    });
  }
}

customElements.define('google-reviews-nav', GoogleReviewsNav);
